import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatProviderSiteOptionLabel, formatContactOptionLabel } from '../../../utils/providerDisplayLabel';
import SearchableProviderTeamOfficeSelect from '../../SearchableProviderTeamOfficeSelect';
import EyeIcon from '../../icons/EyeIcon';
import { hasPermission } from '../../../utils/permissions';
import DeleteIconButton from '../../DeleteIconButton';

function ContactsProvidersSection({
  formData,
  handleChange,
  onPediatricianOfficeChange,
  pediatricianDisplayLabel,
  onSpecialistOfficeChange,
  specialistDisplayLabel,
  clientContacts,
  setClientContacts,
  onSelectContact,
  onBeforeNavigateToNewContact,
  onBeforeNavigateToNewProvider,
  searchContacts,
  contacts,
  schools,
  relationships,
  clientId,
  tenantPath,
}) {
  const navigate = useNavigate();
  const p = tenantPath || ((path) => path);
  const canCreateContact = hasPermission('contacts.create');
  const canCreateProvider = hasPermission('providers.create');

  const [activeContactRow, setActiveContactRow] = useState(null);
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
  const contactCloseTimerRef = useRef(null);

  const runContactSearch = useCallback((q) => {
    if (!searchContacts || !q || String(q).trim().length < 2) {
      setContactSearchResults([]);
      return;
    }
    setContactSearchLoading(true);
    searchContacts(q)
      .then((data) => setContactSearchResults(Array.isArray(data) ? data : []))
      .catch(() => setContactSearchResults([]))
      .finally(() => setContactSearchLoading(false));
  }, [searchContacts]);

  useEffect(() => {
    const t = setTimeout(() => runContactSearch(contactSearchTerm), 300);
    return () => clearTimeout(t);
  }, [contactSearchTerm, runContactSearch]);

  const updateContact = (index, field, value) => {
    const next = [...(clientContacts || [])];
    if (!next[index]) return;
    next[index] = { ...next[index], [field]: value };
    setClientContacts(next);
  };

  const setPrimary = (index) => {
    const next = (clientContacts || []).map((row, i) => ({
      ...row,
      is_primary: i === index,
    }));
    setClientContacts(next);
  };

  const addContact = () => {
    const list = clientContacts || [];
    setClientContacts([...list, { contact_id: '', relationship_id: '', is_primary: false, sort_order: list.length }]);
  };

  const removeContact = (index) => {
    const next = (clientContacts || []).filter((_, i) => i !== index);
    const hasPrimary = next.some((r) => r.is_primary);
    if (next.length > 0 && !hasPrimary) next[0].is_primary = true;
    setClientContacts(next.length > 0 ? next : [{ contact_id: '', relationship_id: '', is_primary: true, sort_order: 0 }]);
  };

  const getContactLabel = (row) => {
    if (row.contact) {
      return formatContactOptionLabel(row.contact);
    }
    const c = (contacts || []).find((x) => String(x.id) === String(row.contact_id));
    return c ? formatContactOptionLabel(c) : row.contact_id || '';
  };

  const contactList = Array.isArray(clientContacts) && clientContacts.length > 0 ? clientContacts : [{ contact_id: '', relationship_id: '', is_primary: true, sort_order: 0 }];

  const pediatricianFieldValue =
    formData.pediatrician_site_id != null && formData.pediatrician_site_id !== ''
      ? String(formData.pediatrician_site_id)
      : formData.pediatrician_id
        ? `provider:${formData.pediatrician_id}`
        : '';

  const specialistFieldValue =
    formData.specialist_site_id != null && formData.specialist_site_id !== ''
      ? String(formData.specialist_site_id)
      : formData.specialist_id
        ? `provider:${formData.specialist_id}`
        : '';

  return (
    <div className="form-section">
      <div className="section-title-row">
        <h3 className="section-title">Contacts, School &amp; Providers</h3>
        <div className="section-title-actions">
          <button type="button" className="btn-add-contact" onClick={addContact} title="Add an existing contact to this client">
            Add contact
          </button>
        </div>
      </div>
      <div className="form-grid">
        {contactList.map((row, index) => (
          <div key={index} className="client-contact-row">
            <div className="form-group client-contact-contact client-contact-searchable-wrap">
              <div className="client-contact-label-row">
                <label>{index === 0 ? 'Contact one *' : `Contact ${index + 1}`}</label>
                {index === 0 && canCreateContact && (
                  <button
                    type="button"
                    className="btn-new-contact-icon"
                    onClick={() => {
                      if (!clientId) onBeforeNavigateToNewContact?.(formData, clientContacts);
                      navigate(p('/contact/new'), { state: clientId ? { returnToClientId: clientId } : { returnToPath: p('/client/new?from=new-client-referral') } });
                    }}
                    title="Create a new contact in the system, then return here to link them to this client"
                    aria-label="Add new contact"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                )}
              </div>
              {row.contact_id ? (
                <div className="client-contact-searchable-selected">
                  <span className="client-contact-selected-label">{getContactLabel(row)}</span>
                  <div className="client-contact-field-icons">
                    <button
                      type="button"
                      className="client-contact-btn-view"
                      onClick={() => navigate(clientId ? p(`/client/${clientId}/contact/${row.contact_id}`) : p(`/contact/${row.contact_id}`), { state: clientId ? { returnToClientId: clientId } : {} })}
                      title="View contact"
                      aria-label="View contact"
                    >
                      <svg className="client-contact-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="client-contact-btn-change"
                      onClick={() => updateContact(index, 'contact_id', '')}
                      title="Change contact"
                      aria-label="Change contact"
                    >
                      <svg className="client-contact-btn-change-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="client-contact-searchable-inner">
                  <input
                    type="text"
                    className="client-contact-search-input"
                    placeholder="Type to search contacts (name)..."
                    value={activeContactRow === index ? contactSearchTerm : ''}
                    onChange={(e) => {
                      setActiveContactRow(index);
                      setContactSearchTerm(e.target.value);
                      setContactDropdownOpen(true);
                    }}
                    onFocus={() => {
                      if (contactCloseTimerRef.current) clearTimeout(contactCloseTimerRef.current);
                      setActiveContactRow(index);
                      setContactDropdownOpen(true);
                    }}
                    onBlur={() => {
                      contactCloseTimerRef.current = setTimeout(() => {
                        setContactDropdownOpen(false);
                      }, 200);
                    }}
                    autoComplete="off"
                    aria-expanded={activeContactRow === index && contactDropdownOpen}
                    aria-controls={`client-contact-results-${index}`}
                  />
                  {activeContactRow === index && contactSearchLoading && (
                    <span className="client-contact-search-loading" aria-live="polite">Searching…</span>
                  )}
                  {activeContactRow === index && contactDropdownOpen && (contactSearchTerm.length >= 2 || contactSearchResults.length > 0) && (
                    <ul id={`client-contact-results-${index}`} className="client-contact-search-results" role="listbox">
                      {contactSearchResults.length === 0 && !contactSearchLoading && (
                        <li className="client-contact-search-no-results">Type at least 2 characters to search.</li>
                      )}
                      {contactSearchResults.map((c) => (
                        <li
                          key={c.id}
                          role="option"
                          className="client-contact-search-option"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            if (onSelectContact) onSelectContact(index, c);
                            setContactSearchTerm('');
                            setContactSearchResults([]);
                            setContactDropdownOpen(false);
                          }}
                        >
                          {formatContactOptionLabel(c)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="form-group client-contact-relationship">
              <label>{index === 0 ? 'Relationship *' : 'Relationship'}</label>
              <select
                value={row.relationship_id || ''}
                onChange={(e) => updateContact(index, 'relationship_id', e.target.value)}
                required={index === 0}
              >
                <option value="">Select a relationship...</option>
                {(relationships || []).map((rel) => (
                  <option key={rel.id} value={rel.id}>
                    {rel.description}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group client-contact-primary">
              <label className="client-contact-primary-label">Primary</label>
              <div className="client-contact-primary-control">
                <input
                  type="radio"
                  name="primary_contact"
                  id={`primary_contact_${index}`}
                  checked={!!row.is_primary}
                  onChange={() => setPrimary(index)}
                  title="Primary contact"
                />
              </div>
            </div>
            <div className="form-group client-contact-actions">
              <span className="client-contact-actions-spacer" aria-hidden="true" />
              <div className="client-contact-actions-btns">
                <DeleteIconButton
                  className="btn-delete"
                  onClick={() => removeContact(index)}
                  disabled={contactList.length <= 1}
                  title="Remove contact"
                  aria-label="Remove contact"
                />
              </div>
            </div>
          </div>
        ))}

        <div id="client-section-school-providers" className="client-form-anchor-target client-form-school-row">
          <div className="form-group">
            <label htmlFor="client-form-school-select">School</label>
            <div className="client-form-school-select-wrap">
              <select
                id="client-form-school-select"
                name="school_id"
                value={formData.school_id || ''}
                onChange={handleChange}
              >
                <option value="">Select a school...</option>
                {(schools || []).map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.district} - {school.district_name}
                  </option>
                ))}
              </select>
              {formData.school_id ? (
                <button
                  type="button"
                  className="client-contact-btn-view client-contact-btn-view-after-select"
                  onClick={() =>
                    navigate(
                      clientId
                        ? p(`/client/${clientId}/school/${formData.school_id}`)
                        : p(`/school/${formData.school_id}`)
                    )
                  }
                  title="View school district"
                  aria-label="View school district"
                >
                  <EyeIcon size={18} title="" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="client-form-pediatrician-row">
          <div className="form-group pediatrician-with-new client-form-pediatrician-wide">
            <div className="pediatrician-label-row">
              <label htmlFor="client-form-pediatrician-office">Pediatrician</label>
              {canCreateProvider && (
                <button
                  type="button"
                  className="btn-new-pediatrician-icon"
                  onClick={() => {
                    if (!clientId) onBeforeNavigateToNewProvider?.(formData, clientContacts);
                    if (clientId) {
                      navigate(p(`/client/${clientId}/provider/new`));
                    } else {
                      navigate(p('/provider/new'), { state: { returnToPath: p('/client/new?from=new-client-referral') } });
                    }
                  }}
                  title="Create a new provider (pediatrician) in the system, then return here to select them for this client"
                  aria-label="Add new pediatrician"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              )}
            </div>
            <div className="client-form-pediatrician-select-wrap">
              <SearchableProviderTeamOfficeSelect
                id="client-form-pediatrician-office"
                typeCode="DR"
                formatOptionLabel={formatProviderSiteOptionLabel}
                hiddenInputName="pediatrician_site_id"
                value={pediatricianFieldValue}
                displayLabel={pediatricianDisplayLabel}
                onChange={onPediatricianOfficeChange}
                required
                placeholder="Click and type pediatrician last name, first name, or DR…"
                searchPlaceholder="Last name, first name, or type code…"
                resultHint="Search doctors (DR) by name or type code — up to 25 offices."
              />
              {formData.pediatrician_id ? (
                <button
                  type="button"
                  className="client-contact-btn-view client-contact-btn-view-after-select"
                  onClick={() =>
                    navigate(
                      clientId
                        ? p(`/client/${clientId}/provider/${formData.pediatrician_id}`)
                        : p(`/provider/${formData.pediatrician_id}`)
                    )
                  }
                  title="View pediatrician"
                  aria-label="View pediatrician"
                >
                  <EyeIcon size={18} title="" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="client-form-pediatrician-row">
          <div className="form-group pediatrician-with-new client-form-pediatrician-wide">
            <div className="pediatrician-label-row">
              <label htmlFor="client-form-specialist-office">Specialist</label>
              {canCreateProvider && (
                <button
                  type="button"
                  className="btn-new-pediatrician-icon"
                  onClick={() => {
                    if (!clientId) onBeforeNavigateToNewProvider?.(formData, clientContacts);
                    if (clientId) {
                      navigate(p(`/client/${clientId}/provider/new`));
                    } else {
                      navigate(p('/provider/new'), { state: { returnToPath: p('/client/new?from=new-client-referral') } });
                    }
                  }}
                  title="Create a new provider (specialist) in the system, then return here to select them for this client"
                  aria-label="Add new specialist"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              )}
            </div>
            <div className="client-form-pediatrician-select-wrap">
              <SearchableProviderTeamOfficeSelect
                id="client-form-specialist-office"
                typeCode="DR"
                formatOptionLabel={formatProviderSiteOptionLabel}
                hiddenInputName="specialist_site_id"
                value={specialistFieldValue}
                displayLabel={specialistDisplayLabel}
                onChange={onSpecialistOfficeChange}
                required={false}
                placeholder="Optional — search by last name, first name, or DR…"
                searchPlaceholder="Last name, first name, or type code…"
                resultHint="Search doctors (DR) by name or type code — up to 25 offices."
              />
              {formData.specialist_id ? (
                <button
                  type="button"
                  className="client-contact-btn-view client-contact-btn-view-after-select"
                  onClick={() =>
                    navigate(
                      clientId
                        ? p(`/client/${clientId}/provider/${formData.specialist_id}`)
                        : p(`/provider/${formData.specialist_id}`)
                    )
                  }
                  title="View specialist"
                  aria-label="View specialist"
                >
                  <EyeIcon size={18} title="" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactsProvidersSection;
